import { useState, useCallback, useMemo } from 'react';
import { Order } from '@/api/orders.api';
import { 
  CurrencyTotals, 
  createEmptyCurrencyTotals, 
  addOrderAmountsToTotals 
} from '@/utils/orders.utils';

export type { CurrencyTotals };

export interface CalculatorTotals {
  [currency: string]: CurrencyTotals;
}

export interface UseOrdersCalculatorReturn {
  isCalculatorMode: boolean;
  selectedOrderIds: Set<string>;
  toggleCalculatorMode: () => void;
  toggleOrderSelection: (order: Order, allOrders: Order[]) => void;
  clearSelection: () => void;
  isOrderSelected: (orderId: string) => boolean;
  getCalculatedTotals: () => CalculatorTotals;
  selectedOrdersCount: number;
}

export function useOrdersCalculator(): UseOrdersCalculatorReturn {
  const [isCalculatorMode, setIsCalculatorMode] = useState(false);
  const [selectedOrderIds, setSelectedOrderIds] = useState<Set<string>>(new Set());
  const [selectedOrdersMap, setSelectedOrdersMap] = useState<Map<string, Order>>(new Map());

  const clearAllSelections = useCallback(() => {
    setSelectedOrderIds(new Set());
    setSelectedOrdersMap(new Map());
  }, []);

  const exitCalculatorMode = useCallback(() => {
    setIsCalculatorMode(false);
    clearAllSelections();
  }, [clearAllSelections]);

  const enterCalculatorMode = useCallback(() => {
    setIsCalculatorMode(true);
  }, []);

  const toggleCalculatorMode = useCallback(() => {
    if (isCalculatorMode) {
      exitCalculatorMode();
    } else {
      enterCalculatorMode();
    }
  }, [isCalculatorMode, exitCalculatorMode, enterCalculatorMode]);

  const getOrderKey = (order: Order) => `${order.mezzaOrderId}:${order.simpleIdentifier}`;

  const addOrderToSelection = useCallback((order: Order) => {
    setSelectedOrderIds((prev) => new Set(prev).add(order.mezzaOrderId));
    setSelectedOrdersMap((prev) => new Map(prev).set(getOrderKey(order), order));
  }, []);

  const removeOrderFromSelection = useCallback((orderId: string) => {
    setSelectedOrderIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(orderId);
      return newSet;
    });
    setSelectedOrdersMap((prev) => {
      const newMap = new Map(prev);
      // Remove all entries for this mezzaOrderId
      for (const key of newMap.keys()) {
        if (key.startsWith(`${orderId}:`)) {
          newMap.delete(key);
        }
      }
      return newMap;
    });
  }, []);

  const toggleOrderSelection = useCallback((order: Order, allOrders: Order[]) => {
    if (selectedOrderIds.has(order.mezzaOrderId)) {
      removeOrderFromSelection(order.mezzaOrderId);
    } else {
      // Add all orders with the same mezzaOrderId (split payment orders)
      const relatedOrders = allOrders.filter(o => o.mezzaOrderId === order.mezzaOrderId);
      relatedOrders.forEach(addOrderToSelection);
    }
  }, [selectedOrderIds, addOrderToSelection, removeOrderFromSelection]);


  const isOrderSelected = useCallback(
    (orderId: string) => {
      return selectedOrderIds.has(orderId);
    },
    [selectedOrderIds]
  );

  const getCalculatedTotals = useCallback((): CalculatorTotals => {
    const totals: CalculatorTotals = {};

    selectedOrdersMap.forEach((order) => {
      const currency = order.currency;
      
      if (!totals[currency]) {
        totals[currency] = createEmptyCurrencyTotals();
      }

      addOrderAmountsToTotals(totals[currency], order);
    });

    return totals;
  }, [selectedOrdersMap]);

  const selectedOrdersCount = useMemo(() => selectedOrdersMap.size, [selectedOrdersMap]);

  return {
    isCalculatorMode,
    selectedOrderIds,
    toggleCalculatorMode,
    toggleOrderSelection,
    clearSelection: clearAllSelections,
    isOrderSelected,
    getCalculatedTotals,
    selectedOrdersCount,
  };
}
